/**
 * This is a library file but required modifications to work in our stack.
 */
import isNaN from 'lodash/isNaN'
import { requestAnimationFrame, cancelAnimationFrame } from '../../services/animate/index'

const SVGNAMESPACE = 'http://www.w3.org/2000/svg'
const STROKEWIDTH = 20
const HANDLERRADIUS = (STROKEWIDTH / 2) + 2
const TOLERANCE = 40

/**
 * Creates root svg to which all sliders residing in the same container are later appended.
 * @returns {SVGCircleElement}
 */
function createRootSVG (boxSize) {
  const svg = document.createElementNS(SVGNAMESPACE, 'svg')
  // let's keep it a square
  svg.setAttributeNS(null, 'id', 'sliderRootSVG')
  svg.setAttributeNS(null, 'width', boxSize)
  svg.setAttributeNS(null, 'height', boxSize)
  svg.setAttributeNS(null, 'viewBox', '-200 -200 400 400')
  return svg
}

export default class CircularSlider {
  defaults = {
    container: 'slider',
    color: 'green',
    max: 100,
    min: 0,
    step: 1,
    radius: 50,
  }

  constructor (options) {
    this.options = { ...this.defaults, ...options }

    this.validateOptions()
    this.init()
    this.timeoutId = null
  }

  /** Method to be called when destroying this class */
  destroy () {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId)
    }
    if (this.timeoutIdAnimationFrame) {
      cancelAnimationFrame(this.timeoutIdAnimationFrame)
    }
  }

  /**
   * Returns the current value which can only be a number divisible by step.
   * @returns {number}
   */
  currentValue () {
    return this.options.min + (this.currentStepNo * this.options.step)
  }

  /**
   * Sets slider value based on the provided step number. We
   * don't allow setting the value directly since
   * it can only be set to numbers divisible by step (this.options.step)
   */
  stepNo (stepNo) {
    const maxSteps = (this.options.max - this.options.min) / this.options.step
    if (isNaN(parseFloat(stepNo)) || stepNo < 0 || stepNo > maxSteps) {
      throw new Error(`Step number ${stepNo} is not between 0 and ${maxSteps}`)
    }

    const radiansEnd = this.step2Rad(stepNo)
    const newPosition = this.calculateNewPosition(radiansEnd)
    if (!this.slider || !this.handle) {
      return
    }

    this.slider.style.transition = 'stroke-dashoffset 0.5s ease-in-out'
    this.handle.style.transition = 'all 0.5s ease-in-out'

    if (this.timeoutIdAnimationFrame) {
      cancelAnimationFrame(this.timeoutIdAnimationFrame)
    }
    this.timeoutIdAnimationFrame = requestAnimationFrame(() => {
      if (!this.slider || !this.handle) {
        return
      }
      this.slider.setAttributeNS(null, 'stroke-dashoffset', `${this.circumference - newPosition.path}`)
      this.handle.style.transform = `rotate(${newPosition.degrees}deg)`
      this.updateState(newPosition, stepNo)
    })
  }

  /**
   * Used for Edge/IE because of it doesn't support SVG animation via CSS. :/
   * Animates slider by setting stroke-dashoffset and transform properties in
   * a requestAnimationFrame loop.
   *
   * @param stepNo
   */
  setStepFallback (stepNo) {
    // stop current animation if in progress
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId)
      this.animationFrameId = null
    }

    // calculate start/end points
    const radiansStart = this.position.radians
    const radiansEnd = this.step2Rad(stepNo)
    const isIncreasing = radiansStart < radiansEnd
    let radiansMove = radiansStart

    const animate = () => {
      // add easing if we are close to end of the step
      // eslint-disable-next-line no-nested-ternary
      radiansMove += (Math.abs(radiansEnd - radiansMove) <= 0.2)
        ? (isIncreasing ? 0.01 : -0.01) : (isIncreasing ? 0.05 : -0.05)

      this.move(radiansMove)

      // allow for a small error because of the rounding
      if ((Math.abs(radiansMove - radiansEnd) > 0.01)) {
        this.animationFrameId = requestAnimationFrame(animate)
      } else {
        this.animationFrameId = null // animation ended
      }
    }

    this.animationFrameId = requestAnimationFrame(animate)
  }

  /**
   * Moves slider on the orbit for the given coordinates.
   *
   * @param angelRadians
   */
  move (angelRadians) {
    const newPosition = this.calculateNewPosition(angelRadians)
    if (!this.canMove(newPosition)) {
      return
    }
    const nextStep = this.deg2Step(newPosition.degrees)
    this.updateState(newPosition, nextStep)
    if (!this.slider || !this.handle) {
      return
    }
    this.slider.style.transition = ''
    this.handle.style.transition = ''
    requestAnimationFrame(() => {
      this.slider.setAttributeNS(null, 'stroke-dashoffset', `${this.circumference - newPosition.path}`)
      this.handle.style.transform = `rotate(${newPosition.degrees}deg)`
    })
  }

  /**
   * Returns false if slider wants to be moved past the top zero point.
   * @param newPosition
   * @returns {boolean}
   */
  canMove (newPosition) {
    return !(this.position.y < 0 && ((this.position.x >= 0 && newPosition.x < 0)
      || (this.position.x < 0 && newPosition.x >= 0)))
  }

  /**
   * Calculates new position, angles and path traveled based on local coordinate
   * system (center = 0,0)
   * @param {Number} angleRadians
   * @returns {{x: number, y: number, degrees: number, radians: number, path: number}}
   */
  calculateNewPosition (angleRadians) {
    const newX = Math.round(Math.sin(angleRadians) * this.radius)
    const newY = Math.round(Math.cos(angleRadians) * this.radius) * -1

    // we have our coordinates right, but angles need to be adjusted to positive number
    // basically just add 2PI - 360 degrees
    const radians360 = angleRadians < 0 ? angleRadians + 2 * Math.PI : angleRadians
    const angelDegrees = (radians360 * 180.0) / Math.PI
    const path = Math.round(this.radius * radians360)

    return {
      x: Math.floor(angelDegrees) === 359 ? -1 : newX,
      y: newY,
      degrees: angelDegrees,
      radians: radians360,
      path,
    }
  }

  updateState (newPosition, nextStep) {
    // notify about value change
    if (this.currentStepNo !== nextStep && (this.options.valueChange && typeof (this.options.valueChange) === 'function')) {
      this.currentStepNo = nextStep // set step here so we send the latest value
      this.options.valueChange(this.currentValue)
    }

    // update slider internal state
    this.value = this.deg2Val(newPosition.degrees)
    this.currentStepNo = nextStep
    this.position = newPosition
  }

  validateOptions () {
    const { step, min, max } = this.options
    if (min > max) {
      throw new Error(`Min ${min} must be smaller than max ${max}!`)
    }
    if (max % step !== 0 || min % step !== 0) {
      throw new Error(`Min ${min} and max ${max} + must be divisible by step ${step}!`)
    }
    if (this.options.radius > 200 || this.options.radius < 0) {
      throw new Error('Radius must be between 1 and 200. The slider will adjust the to the size of the container automatically. Radius 200 means slider will be touching the boundaries')
    }
  }

  /**
   * Initializes (calculates values of) all properties and creates a slider.
   */
  init () {
    this.centerX = 0
    this.centerY = 0
    this.radius = this.options.radius - (STROKEWIDTH / 2) // subtract border width from radius
    this.circumference = this.options.radius * 2 * Math.PI
    this.currentStepNo = 0
    this.isDragging = false
    this.position = this.calculateNewPosition(this.centerX)
    this.value = this.options.min
    this.lastTouchType = ''
    this.animationFrameId = null
    this.initSlider()
    this.initEventHandlers()
  }

  /**
   * Creates slider composed of underlying stripped SVG circle and top
   * colored circle which will behave as slider.
   */
  initSlider () {
    this.container = document.getElementById(this.options.container)
    if (!this.container) {
      return
    }
    // create root svg only when the first slider is added to the container.
    this.rootSVG = document.getElementById('sliderRootSVG')
    if (!this.rootSVG) {
      this.rootSVG = createRootSVG(this.container.offsetWidth)
      this.container.appendChild(this.rootSVG)
    }
    this.slider = this.createSliderCircle()
    this.handle = this.createHandle()
    this.clickCircle = this.createClickCircle()
    this.rootSVG.appendChild(this.createEmptyCircle())
    this.rootSVG.appendChild(this.clickCircle)
    this.rootSVG.appendChild(this.slider)
    this.rootSVG.appendChild(this.handle)
  }

  /**
   * Transform the client to local cordinates
   * @returns {null|{x: number, y: number}} Returns cordinates object or null if
   * this.rootSVG does not exist
   */
  transformClientToLocalCoordinate (svgPoint, event) {
    if (!this.rootSVG) {
      return null
    }
    // eslint-disable-next-line no-param-reassign
    svgPoint.x = event.clientX
    // eslint-disable-next-line no-param-reassign
    svgPoint.y = event.clientY

    return svgPoint.matrixTransform(this.rootSVG.getScreenCTM().inverse())
  }

  /**
   * Creates new SVG circle used as a top slider.
   */
  createSliderCircle () {
    const slider = this.createCircle()
    if (!slider) {
      return null
    }
    slider.setAttributeNS(null, 'class', 'top-slider')
    slider.setAttributeNS(null, 'transform', 'rotate(-90)')
    slider.setAttributeNS(null, 'stroke-dasharray', `${this.circumference} ${this.circumference}`)
    slider.setAttributeNS(null, 'stroke-dashoffset', `${this.circumference}`)

    slider.style.stroke = this.options.color
    slider.style.strokeWidth = `${STROKEWIDTH}px`

    return slider
  }

  /**
   * Creates transparent circle so we can click on it (dashed border is not click-able everywhere)
   * @returns SVG
   */
  createClickCircle () {
    const slider = this.createCircle()
    slider.style.strokeWidth = `${STROKEWIDTH}px`
    slider.style.stroke = 'transparent'
    return slider
  }

  /**
   * Creates new SVG circle with dashed border used as empty "underlying" slider.
   */
  createEmptyCircle () {
    const slider = this.createCircle()
    slider.setAttributeNS(null, 'class', 'dashed-circle')
    slider.setAttributeNS(null, 'transform', 'rotate(-90)')
    slider.style.strokeWidth = `${STROKEWIDTH}px`
    slider.style.strokeDasharray = '5, 2'
    return slider
  }

  /**
   * Creates new SVG circle element based on passed options.
   * @returns {SVGCircleElement}
   */
  createCircle () {
    const slider = document.createElementNS(SVGNAMESPACE, 'circle')
    slider.setAttributeNS(null, 'cx', this.centerX)
    slider.setAttributeNS(null, 'cy', this.centerY)
    slider.setAttributeNS(null, 'r', this.radius)
    slider.setAttributeNS(null, 'fill', 'none')
    return slider
  }

  /**
   * Creates a handle for the slider.
   */
  createHandle () {
    const handle = document.createElementNS(SVGNAMESPACE, 'circle')
    handle.setAttributeNS(null, 'cx', `${this.centerX}`)
    handle.setAttributeNS(null, 'cy', `${this.centerY - this.radius}`)
    handle.setAttributeNS(null, 'r', `${HANDLERRADIUS}`)
    handle.setAttributeNS(null, 'fill', '#fff')
    handle.setAttributeNS(null, 'class', 'handle')
    handle.setAttributeNS(null, 'id', `handle${this.options.container}${this.radius}`) // add uniqueId
    return handle
  }

  deg2Step (deg) {
    const val = this.deg2Val(deg)
    return this.val2Step(val)
  }

  deg2Val (deg) {
    const range = this.options.max - this.options.min
    return Math.round(deg * (range / 360.0)) + this.options.min
  }

  val2Step (val) {
    return Math.round((val - this.options.min) / this.options.step)
  }

  step2Rad (stepNo) {
    const val = stepNo * this.options.step + this.options.min
    const adjustedVal = val - this.options.min
    const range = this.options.max - this.options.min
    const degrees = this.options.max === val
      ? 359.99
      : (Math.round(adjustedVal * (360.0 / range))) % 360

    return Math.round((degrees * Math.PI) / (180 * 100)) / 100
  }

  point2Radians (x, y) {
    // calculate distance from rotated circle (0° is on top)
    // replacing x and y in Math.atan2 method rotates the axis for 90 degrees but in wrong direction
    // multiply Y with -1 to "rotate" for 180° in the right direction :)
    return Math.atan2(x - this.centerX, -y - this.centerY)
  }

  initEventHandlers () {
    this.container.addEventListener('mousemove', (e) => this.handleDrag(e))
    this.container.addEventListener('mouseup', (e) => this.cancelDrag(e))
    this.container.addEventListener('mouseleave', (e) => this.cancelDrag(e))

    this.handle.addEventListener('touchmove', (e) => this.touchHandler(e))
    this.container.addEventListener('touchcancel', (e) => this.touchHandler(e))
    this.container.addEventListener('touchend', (e) => this.touchHandler(e))

    this.clickCircle.addEventListener('click', (e) => this.handleSliderClick(e))
    this.clickCircle.addEventListener('touchend', (e) => this.touchHandler(e))
    this.clickCircle.addEventListener('touchstart', (e) => this.touchHandler(e))

    this.slider.addEventListener('click', (e) => this.handleSliderClick(e))
    this.slider.addEventListener('touchend', (e) => this.touchHandler(e))
    this.slider.addEventListener('touchstart', (e) => this.touchHandler(e))

    this.handle.addEventListener('touchstart', (e) => this.touchHandler(e))
    this.handle.addEventListener('mousedown', (e) => this.startDrag(e))
  }

  startDrag (e) {
    e.preventDefault()
    this.isDragging = true
  }

  /**
   * Handles drag as long as the touch/mouse is inside the tolerance radius.
   * @param e
   */
  handleDrag (e) {
    e.preventDefault()
    if (!this.isDragging) {
      return
    }

    const svgPoint = this.rootSVG.createSVGPoint()
    const localCoords = this.transformClientToLocalCoordinate(svgPoint, e)
    if (!localCoords) {
      return
    }
    const mouseHandleOffsetX = this.position.x - localCoords.x
    const mouseHandleOffsetY = this.position.y - localCoords.y
    if (mouseHandleOffsetX > TOLERANCE || mouseHandleOffsetY > TOLERANCE) {
      this.cancelDrag(e)
    } else {
      const angelRadians = this.point2Radians(localCoords.x, localCoords.y)
      this.move(angelRadians)
    }
  }

  /**
   * Cancels drag and finishes the move by scrolling to the closest step.
   *
   * @param e
   */
  cancelDrag (e) {
    e.preventDefault()
    // only complete step if you are currently moving
    if (this.isDragging) {
      this.stepNo = this.val2Step(this.value)
    }

    this.isDragging = false
  }

  handleSliderClick (e) {
    const svgPoint = this.rootSVG.createSVGPoint()
    const localCoords = this.transformClientToLocalCoordinate(svgPoint, e)
    const newPosition = this.calculateNewPosition(
      this.point2Radians(localCoords.x, localCoords.y),
    )
    const nextStep = this.deg2Step(newPosition.degrees)

    if (this.currentStepNo === nextStep) {
      this.handle.classList.add('same-step-error')
      if (this.timeoutId) {
        clearTimeout(this.timeoutId)
      }
      this.timeoutId = setTimeout(() => this.handle.classList.remove('same-step-error'), 300)
    } else {
      this.stepNo = nextStep
    }
  }

  touchHandler (e) {
    const touches = e.changedTouches

    // Ignore multi-touch
    if (touches.length > 1) {
      return
    }

    const touch = touches[0]
    const events = ['touchstart', 'touchmove', 'touchend', 'touchcancel']
    const mouseEvents = ['mousedown', 'mousemove', 'mouseup', 'mouseleave']
    const ev = events.indexOf(e.type)

    if (ev === -1) {
      return
    }

    const type = e.type === events[2] && this.lastTouchType === events[0] ? 'click' : mouseEvents[ev]
    const simulatedEvent = document.createEvent('MouseEvent')
    simulatedEvent.initMouseEvent(
      type,
      true,
      true,
      window,
      1,
      touch.screenX,
      touch.screenY,
      touch.clientX,
      touch.clientY,
      false,
      false,
      false,
      false,
      0,
      null,
    )

    touch.target.dispatchEvent(simulatedEvent)
    e.preventDefault()
    this.lastTouchType = e.type
  }
}
